import classNames from 'classnames';
import * as React from 'react';
import { DetailedHTMLProps, useMemo } from 'react';
import Link from '../Link';
import './style.scss';

export type TextColors =
  | 'light'
  | 'dark'
  | 'primary'
  | 'secondary'
  | 'alternative1'
  | 'alternative2'
  | 'moto-orange'
  | 'red-orange'
  | 'tuft-bush'
  | 'brown'
  | 'dark-grey'
  | 'tangery-moto'
  | 'be-amazing-moto'
  | 'check-my-bus-moto'
  | 'holodomor-moto'
  | 'yangol-moto'
  | 'humanoo-moto'
  | 'fittracks-moto'
  | 'bidroom-moto'
  | 'dodger-blue'
  | 'norsk-motto'
  | 'deftpower-motto'
  | 'sportplus-motto'
  | 'forceusa-motto'
  | 'artofcomms-motto';

export type TextTypes =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'p-updated'
  | 'button'
  | 'label'
  | 'caption';

export type FontWeights = 'regular' | 'medium' | 'demi-bold' | 'bold';

export type FontOpasity = '60' | 'light-60' | '40' | 'dark-40' | '30' | '20';

export type TextAlign = 'left' | 'center' | 'right' | 'justify';

export interface Props {
  type: TextTypes;
  color?: TextColors;
  weight?: FontWeights;
  uppercase?: boolean;
  dim?: FontOpasity;
  align?: TextAlign;
  href?: string;
  language?: string;
  linkClassName?: string;
  className?: string;
  children: any;
  id?: string;
  style?: React.CSSProperties;
  itemProp?: string;
  itemScope?: boolean;
  itemType?: string;
}

const Text: React.SFC<Props> = ({
  type,
  color = 'dark',
  weight = 'regular',
  uppercase = false,
  dim,
  href,
  language,
  linkClassName,
  align = '',
  className = '',
  children,
  id,
  style,
  ...props
}) => {
  const classNameExtended = classNames(
    color,
    weight,
    align,
    {
      uppercase: uppercase,
      [`dim-${dim}`]: !!dim,
    },
    className
  );

  const commonProps = useMemo<
    DetailedHTMLProps<
      React.HTMLAttributes<HTMLHeadingElement>,
      HTMLHeadingElement
    >
  >(
    () => ({
      className: classNameExtended,
      id,
      style,
    }),
    [classNameExtended, id, style]
  );

  if (href) {
    return (
      <Link
        href={href}
        className={classNames('label', classNameExtended, linkClassName)}
        language={language}
      >
        {children}
      </Link>
    );
  }

  switch (type) {
    case 'h1':
      return <h1 {...commonProps}>{children}</h1>;

    case 'h2':
      return <h2 {...commonProps}>{children}</h2>;

    case 'h3':
      return <h3 {...commonProps}>{children}</h3>;

    case 'h4':
      return <h4 {...commonProps}>{children}</h4>;

    case 'h5':
      return <h5 {...commonProps}>{children}</h5>;

    case 'h6':
      return <h6 {...commonProps}>{children}</h6>;

    case 'p':
      return (
        <p {...commonProps} {...props}>
          {children}
        </p>
      );

    case 'button':
      return <span {...commonProps}>{children}</span>;

    case 'label':
      return (
        <p
          id={id}
          style={style}
          className={classNames('label', classNameExtended)}
          {...props}
        >
          {children}
        </p>
      );

    case 'caption':
      return (
        <p
          id={id}
          style={style}
          className={classNames('caption', classNameExtended)}
        >
          {children}
        </p>
      );

    default:
      return (
        <p style={style} id={id}>
          {children}
        </p>
      );
  }
};

export default Text;
